import React, { useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { PiPlusBold } from "react-icons/pi";
import FlexBox from "../common/flex-box";
import SimpleCard from "../common/simple-card";
import { BUSINESS_CATEGORIES } from "lib/constants";
import { Link, useLocation } from "react-router-dom";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import ProfilePicCard from "./profile-pic-card";
import PractitionerTherapiesCard from "./practitioner/practitioner-therapies";
import SimpleRichTextCard from "../common/simple-rich-text-card";
import PractitionerDocumentsCard from "./practitioner/practitioner-documents-card";
import PractitionerBusinessProfileCard from "./practitioner/practitioner-business-profile-card";
import SimpleDataTable from "../common/simple-data-table";
import { PRACTITIONER_SUBSCRIPTIONS_COMPACT_COLUMNS } from "lib/constants/table-columns";
import ErrorBox from "../common/error-box";
import { AppContext } from "lib/contexts/app-context";
import { isPatientUser, isPractitionerUser } from "lib/utils/util";
import usePractitioner from "lib/hooks/usePractitioner";

interface PractitionerProfileCardProps {
  isEditable?: boolean;
  isLoading?: boolean;
  error?: string;
  practitionerDetails: PractitionerDetails;
  practitionerTherapies?: PractitionerTherapy[];
  therapies?: Therapy[];
  profilePicUrl?: string;
  practitionerDocuments?: PractitionerDocuments;
  practitionerSubscriptions?: PractitionerSubscriptionDetails[];
  onProfileUpdate?: (values: PractitionerDetails) => void;
  onTherapiesUpdate?: (values: PractitionerTherapy[]) => void;
  onTherapyDelete?: (values: PractitionerTherapy) => void;
  onProfilePicUpdate?: (values: File) => void;
  onCertificateUpdate?: (value: File) => void;
}

const PractitionerProfileCard: React.FC<PractitionerProfileCardProps> = (props) => {
  const { appState } = useContext(AppContext);
  const isPatient = isPatientUser(appState?.userRole);
  const isPractitioner = isPractitionerUser(appState?.userRole);
  const {
    isLoading,
    error,
    practitionerTherapies,
    isEditable = true,
    practitionerDetails,
    therapies,
    profilePicUrl,
    practitionerDocuments,
    practitionerSubscriptions,
    onProfileUpdate,
    onTherapiesUpdate,
    onTherapyDelete,
    onProfilePicUpdate,
    onCertificateUpdate,
  } = props;
  const { updatePractitionerDocuments, updatePractitionerQualificationDoc, updatePractitionerInsuranceDoc } = usePractitioner();

  /** Calculated Vars */
  const allClinics = new Set((practitionerSubscriptions ?? []).map((subscription) => subscription.clinicName));
  const clinics = Array.from(allClinics).map((clinic, index) => <li key={index}>{clinic}</li>);
  /** Calculated Vars */

  return (
    <Row className="g-3">
      <Col xs="12">
        <ProfilePicCard
          isLoading={isLoading}
          userProfile={practitionerDetails}
          isEditable={isEditable}
          profilePicUrl={profilePicUrl}
          onProfileUpdate={(values) =>
            onProfileUpdate({ ...practitionerDetails, ...values })
          }
          onProfilePicUpdate={onProfilePicUpdate}
        />
      </Col>
      {error && (
        <Col xs="12">
          <ErrorBox message={error} />
        </Col>
      )}
      <Col xs="12">
        <Row className="g-3">
          <Col sm="7">
            <Row className="g-3">
              <Col xs="12">
                <SimpleRichTextCard
                  isLoading={isLoading}
                  label="About"
                  isEditable={isEditable}
                  value={practitionerDetails?.biography}
                  onContentChange={(value) =>
                    onProfileUpdate({
                      ...practitionerDetails,
                      biography: value,
                    })
                  }
                />
              </Col>
              <Col xs="12">
                <PractitionerTherapiesCard
                  isLoading={isLoading}
                  isEditable={isEditable}
                  therapies={practitionerTherapies}
                  allTherapies={therapies}
                  onAddUpdateTherapies={onTherapiesUpdate}
                  onDeleteTherapies={onTherapyDelete}
                />
              </Col>
            </Row>
          </Col>
          <Col sm="5">
            <Row className="g-3">
              <Col xs="12">
                <PractitionerBusinessProfileCard
                  isEditable={isPractitioner && isEditable}
                  isPractitionerUrl={isPractitioner}
                  isLoading={isLoading}
                  practitionerDetails={practitionerDetails}
                  onChange={onProfileUpdate}
                />
              </Col>
              {practitionerDetails?.businessCategory?.localeCompare(
                BUSINESS_CATEGORIES.ORGANISATION
              ) === 0 && (
                  <>
                    <Col xs="12">
                      <SimpleRichTextCard
                        isLoading={isLoading}
                        label="Business Summary"
                        isEditable={isEditable}
                        value={practitionerDetails?.businessSummary}
                        onContentChange={(value) =>
                          onProfileUpdate({
                            ...practitionerDetails,
                            businessSummary: value,
                          })
                        }
                      />
                    </Col>
                  </>
                )}
              {!isPatient && <Col xs="12">
                <SimpleCard
                  isTitleNoBg
                  title="Subscription Status"
                  action={
                    isEditable && (
                      <Link
                        to={PAGE_ROUTES.PRACTITIONER.Subscription}
                        className="btn btn-primary"
                      >
                        <FlexBox>
                          <>Add</> <PiPlusBold />
                        </FlexBox>
                      </Link>
                    )
                  }
                >
                  <SimpleDataTable
                    className="mt-3"
                    columns={PRACTITIONER_SUBSCRIPTIONS_COMPACT_COLUMNS}
                    data={practitionerSubscriptions ?? []}
                    isShowPagination={false}
                  />
                </SimpleCard>
              </Col>}
              { /** Ideally the clinics block should be shown to patient */}
              {!isPatient && <Col xs="12">
                <SimpleCard isTitleNoBg title="Clinics Operating In">
                  {clinics?.length === 0 ? <p className="mt-3">
                    No clinic association details found
                  </p> : <ul className="mt-3">{clinics}</ul>}

                </SimpleCard>
              </Col>}
              {!isPatient && <Col xs="12">
                <PractitionerDocumentsCard
                  isLoading={isLoading}
                  isEditable={isEditable}
                  documents={practitionerDocuments}
                  onDocumentsUpdate={(
                    profileImage,
                    educationalCertificate,
                    qualificationDoc,
                    insuranceDoc
                  ) => {
                    if (profileImage) onProfilePicUpdate(profileImage);
                    if (educationalCertificate) onCertificateUpdate(educationalCertificate);
                    if (practitionerDetails?.id) {
                      if (qualificationDoc) {
                        updatePractitionerQualificationDoc(
                          practitionerDetails.id,
                          qualificationDoc,
                          () => {
                            // Optionally refresh documents or show toast
                          }
                        );
                      }
                      if (insuranceDoc) {
                        updatePractitionerInsuranceDoc(
                          practitionerDetails.id,
                          insuranceDoc,
                          () => {
                            // Optionally refresh documents or show toast
                          }
                        );
                      }
                    }
                  }}
                />
              </Col>}
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default PractitionerProfileCard;
